import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type ToastKind = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id:      string;
  kind:    ToastKind;
  message: string;
}

interface ToastContextValue {
  toast: (kind: ToastKind, message: string, duration?: number) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────

const KIND_STYLES: Record<ToastKind, string> = {
  success: 'border-terminal-green/40  bg-terminal-green/10  text-terminal-green',
  error:   'border-red-500/40         bg-red-500/10         text-red-400',
  warning: 'border-severity-medium/40 bg-severity-medium/10 text-severity-medium',
  info:    'border-severity-info/40   bg-severity-info/10   text-severity-info',
};

const KIND_ICON: Record<ToastKind, string> = {
  success: '✓',
  error:   '✗',
  warning: '⚠',
  info:    'ℹ',
};

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue>({
  toast: () => undefined,
});

// ─────────────────────────────────────────────────────────────────────────────
// Provider + Container
// ─────────────────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timerMap            = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timerMap.current.get(id);
    if (timer) clearTimeout(timer);
    timerMap.current.delete(id);
  }, []);

  const toast = useCallback(
    (kind: ToastKind, message: string, duration = 4000) => {
      const id = crypto.randomUUID();
      setToasts((prev) => [...prev, { id, kind, message }]);
      const timer = setTimeout(() => dismiss(id), duration);
      timerMap.current.set(id, timer);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed right-4 top-4 z-[100] flex flex-col gap-2">
          <AnimatePresence initial={false}>
            {toasts.map((t) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, x: 32, scale: 0.95 }}
                animate={{ opacity: 1, x: 0,  scale: 1 }}
                exit={{ opacity: 0, x: 32, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                className={`pointer-events-auto flex items-start gap-3 rounded-lg border px-4 py-3 font-mono text-sm shadow-lg ${KIND_STYLES[t.kind]}`}
              >
                <span className="mt-px shrink-0 font-bold">{KIND_ICON[t.kind]}</span>
                <span className="flex-1">{t.message}</span>
                <button
                  type="button"
                  onClick={() => dismiss(t.id)}
                  className="ml-2 shrink-0 opacity-60 hover:opacity-100 transition-opacity"
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useToast() {
  const ctx = useContext(ToastContext);
  return {
    success: (msg: string, dur?: number) => ctx.toast('success', msg, dur),
    error:   (msg: string, dur?: number) => ctx.toast('error',   msg, dur),
    warning: (msg: string, dur?: number) => ctx.toast('warning', msg, dur),
    info:    (msg: string, dur?: number) => ctx.toast('info',    msg, dur),
  };
}
